import { Candle } from '../../../../Detail/types';
import {
  MrcEpisode,
  SingleProfitCalculationApiResponse,
  Transaction,
} from '../../../../ProfitCalculations/types';
import { isCore, isStopLoss } from './format';

export const flattenShorterT = (
  spc: SingleProfitCalculationApiResponse,
): Candle[] => (spc.savedData?.shorterTNormalCandles ?? []).flat();

/** Core transactions sorted oldest-first. */
const coreSortedAsc = (
  spc: SingleProfitCalculationApiResponse,
): Transaction[] =>
  [...spc.transactions]
    .filter((tx) => isCore(tx.transactionType))
    .sort(
      (a, b) =>
        (a.transactionTimestamp ?? a.timestamp) -
        (b.transactionTimestamp ?? b.timestamp),
    );

export interface CandleWindow {
  candles: Candle[];
  start: number;
  end: number;
  transactions: Transaction[];
}

/** Extra candles kept on each side of a window so transaction dots near the
 * edges are not clipped. */
const PAD_CANDLES = 4;

/** Spacing between consecutive candles, defaulting to 1h when unknown. */
const candleInterval = (candles: Candle[]): number =>
  candles.length > 1
    ? candles[1].startTimestamp - candles[0].startTimestamp
    : 3600_000;

/** All candles within [start, end] plus PAD_CANDLES of breathing room either
 * side, so edge transaction dots stay fully on-chart. */
const slicePaddedCandles = (
  allCandles: Candle[],
  start: number,
  end: number,
): Candle[] => {
  const sorted = [...allCandles].sort(
    (a, b) => a.startTimestamp - b.startTimestamp,
  );
  let firstIndex = -1;
  let lastIndex = -1;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].startTimestamp >= start && sorted[i].endTimestamp <= end) {
      if (firstIndex === -1) firstIndex = i;
      lastIndex = i;
    }
  }
  if (firstIndex === -1) return [];
  return sorted.slice(
    Math.max(0, firstIndex - PAD_CANDLES),
    Math.min(sorted.length, lastIndex + 1 + PAD_CANDLES),
  );
};

export const windowForTransaction = (
  txId: number,
  spc: SingleProfitCalculationApiResponse,
): CandleWindow => {
  // Custom transactions (subscription only) carry negated ids and live in their
  // own array; they get the same bounded window as a TP/SL, with a single dot.
  const custom = (spc.customTransactions ?? []).find(
    (candidate) => candidate.id === txId,
  );
  const tx =
    custom ?? spc.transactions.find((candidate) => candidate.id === txId);
  if (!tx) return { candles: [], start: 0, end: 0, transactions: [] };
  const txTimestamp = tx.transactionTimestamp ?? tx.timestamp;
  const sortedCore = coreSortedAsc(spc);

  let start: number;
  let end: number;
  let transactions: Transaction[];

  if (!custom && isCore(tx.transactionType)) {
    // Core transaction: window spans the previous → next core transaction;
    // every transaction inside (3 cores + all TP/SL between) is shown.
    const coreIndex = sortedCore.findIndex((core) => core.id === tx.id);
    start =
      sortedCore[coreIndex - 1]?.transactionTimestamp ??
      sortedCore[coreIndex - 1]?.timestamp ??
      txTimestamp;
    end =
      sortedCore[coreIndex + 1]?.transactionTimestamp ??
      sortedCore[coreIndex + 1]?.timestamp ??
      txTimestamp;
    if (start === end) {
      end = start + 24 * 3600_000;
    }
    transactions = spc.transactions.filter((candidate) => {
      const candidateTimestamp =
        candidate.transactionTimestamp ?? candidate.timestamp;
      return candidateTimestamp >= start && candidateTimestamp <= end;
    });
  } else {
    // TP / SL transaction: the candle window still spans the two bounding
    // core transactions for context, but ONLY two dots are ever drawn — this
    // transaction and the single transaction it is paired with.
    const before = [...sortedCore]
      .reverse()
      .find(
        (core) => (core.transactionTimestamp ?? core.timestamp) <= txTimestamp,
      );
    const after = sortedCore.find(
      (core) => (core.transactionTimestamp ?? core.timestamp) > txTimestamp,
    );
    start = before?.transactionTimestamp ?? before?.timestamp ?? txTimestamp;
    end = after?.transactionTimestamp ?? after?.timestamp ?? Date.now();
    if (start === end) {
      end = start + 24 * 3600_000;
    }
    // A stop-loss and its take-profit are paired 1:1 via originalId: the
    // stop-loss carries originalId = the id of the take-profit it derived
    // from. A stop-loss shows its original take-profit; a take-profit shows
    // the stop-loss derived from it (if any). Never any other transaction.
    const linked = isStopLoss(tx.transactionType)
      ? spc.transactions.find((candidate) => candidate.id === tx.originalId)
      : spc.transactions.find(
          (candidate) =>
            isStopLoss(candidate.transactionType) &&
            candidate.originalId === tx.id,
        );
    transactions = [tx, linked].filter(
      (entry): entry is Transaction => !!entry,
    );
    // Keep both dots inside the candle window.
    for (const dot of transactions) {
      const dotTimestamp = dot.transactionTimestamp ?? dot.timestamp;
      if (dotTimestamp < start) start = dotTimestamp;
      if (dotTimestamp > end) end = dotTimestamp;
    }
  }

  const candles = slicePaddedCandles(flattenShorterT(spc), start, end);
  return { candles, start, end, transactions };
};

export const windowForLiveState = (
  spc: SingleProfitCalculationApiResponse,
): CandleWindow => {
  const sortedCore = coreSortedAsc(spc);
  const lastCore = sortedCore[sortedCore.length - 1];
  const start = lastCore?.transactionTimestamp ?? lastCore?.timestamp ?? 0;
  const end = Date.now();
  const candles = slicePaddedCandles(flattenShorterT(spc), start, end);
  const transactions = spc.transactions.filter(
    (tx) => (tx.transactionTimestamp ?? tx.timestamp) >= start,
  );
  return { candles, start, end, transactions };
};

/**
 * Candle window for the MRC deep dive — zoomed from the start of the MRC
 * episode to just past the core transaction it resolved into.
 */
export const windowForMrcDeepDive = (
  txId: number,
  episode: MrcEpisode,
  spc: SingleProfitCalculationApiResponse,
): CandleWindow => {
  const tx = spc.transactions.find((candidate) => candidate.id === txId);
  const txTimestamp = tx
    ? tx.transactionTimestamp ?? tx.timestamp
    : episode.endTimestamp ?? episode.startTimestamp;

  const allCandles = flattenShorterT(spc);
  const interval = candleInterval(allCandles);

  const start = episode.startTimestamp;
  const end = txTimestamp + 5 * interval;

  const candles = allCandles.filter(
    (candle) => candle.startTimestamp >= start && candle.endTimestamp <= end,
  );
  const transactions = spc.transactions.filter((candidate) => {
    const candidateTimestamp =
      candidate.transactionTimestamp ?? candidate.timestamp;
    return candidateTimestamp >= start && candidateTimestamp <= end;
  });
  return { candles, start, end, transactions };
};

/**
 * Candle window for the live-state MRC deep dive — zoomed to the MRC episode
 * currently in progress (the one that initiated after the latest core
 * transaction). Ends at the episode's end, or now if it is still open.
 */
export const windowForLiveMrcDeepDive = (
  episode: MrcEpisode,
  spc: SingleProfitCalculationApiResponse,
): CandleWindow => {
  const allCandles = flattenShorterT(spc);
  const interval = candleInterval(allCandles);

  const start = episode.startTimestamp;
  const end = (episode.endTimestamp ?? Date.now()) + 5 * interval;

  const candles = allCandles.filter(
    (candle) => candle.startTimestamp >= start && candle.endTimestamp <= end,
  );
  const transactions = spc.transactions.filter((candidate) => {
    const candidateTimestamp =
      candidate.transactionTimestamp ?? candidate.timestamp;
    return candidateTimestamp >= start && candidateTimestamp <= end;
  });
  return { candles, start, end, transactions };
};
